import React from 'react';
import { HeaderPage } from '../../../components/HeaderPage/HeaderPage';
import { useParams } from 'react-router-dom';
import {
  Backdrop,
  Box,
  Button as MuiButton,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Button from 'components/Button';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import TextField from 'components/TextField';
import { useForm } from 'react-hook-form';
import {
  getEscalationCompany,
  getEscalationUsers,
  updateEscalationCompany,
} from 'data/api/company.api';
import { getTriageOptions } from 'data/api/user-note.api';
import TextFieldWithMask from 'components/TextFieldWithMask';
import { toast } from 'react-toastify';
import { validateEmails } from 'utils';
import { handleTabs, TabEnum } from '../company-detail/CompanDetailNew';
import {
  CompanyInfo,
  EscalationRecipient,
  EscalationRecipientRequest,
  EscalationRecipientRole,
} from 'data/models/company.model';
import { TriageOption } from 'data/models/user-note.model';
import { UserOverview } from 'data/models/user.model';
import { useSelector } from 'react-redux';
import { selectCompany } from 'store/companySlide';
import {
  CustomDivider,
  EscalationTriageSection,
  joinNames,
  outOfRoster,
  RecipientFieldValue,
} from './EscalationRecipientsSection';

const formatPhoneNumber = (phoneNumberString?: string) => {
  if (phoneNumberString) {
    phoneNumberString = phoneNumberString.replace('+', '');
    const formattedPhoneNumber = phoneNumberString.replace(
      /(\d{3})(\d{3})(\d{4})/,
      '($1) $2-$3',
    );
    return formattedPhoneNumber;
  }
  return '';
};

// Names must match `triage_options.triage_options_type` from the backend —
// used to resolve the id each category's recipients submit against, and to
// pull that category's recipients back out of the GET response.
const TRIAGE_CATEGORY = {
  clinical: 'Clinical',
  psychosocial: 'Psychosocial',
  memberManagement: 'Member Management',
} as const;

export interface EscalationForm {
  companyPhone: string;
  generalEmail: string;
  googleReviewLink: string;
  supportServices?: string;
  clinicalTo: RecipientFieldValue[];
  clinicalCc: RecipientFieldValue[];
  clinicalTriageNotes?: string;
  psychosocialTo: RecipientFieldValue[];
  psychosocialCc: RecipientFieldValue[];
  psychosocialTriageNotes?: string;
  memberManagementTo: RecipientFieldValue[];
  memberManagementCc: RecipientFieldValue[];
  memberManagementTriageNotes?: string;
}

const recipientsForCategory = (
  recipients: EscalationRecipient[] | undefined,
  triageOptionsType: string,
  role: EscalationRecipientRole,
): RecipientFieldValue[] =>
  (recipients || [])
    .filter(
      (recipient) =>
        recipient.triageOptionsType === triageOptionsType &&
        recipient.role === role,
    )
    .map((recipient) => ({
      userId: recipient.user.userId,
      firstName: recipient.user.firstName,
      lastName: recipient.user.lastName,
      email: recipient.user.email,
    }));

interface RecipientsPayloadResult {
  recipients: EscalationRecipientRequest[];
  // Categories with a selected To/Cc recipient whose triageOptionsId could
  // not be resolved from the loaded options — submitting anyway would PUT an
  // empty set for that category and the backend replaces the full set, so
  // this is silent data loss rather than a no-op.
  missingCategories: string[];
}

const buildRecipientsPayload = (
  values: EscalationForm,
  triageOptions: TriageOption[],
): RecipientsPayloadResult => {
  const optionIdFor = (triageOptionsType: string) =>
    triageOptions.find(
      (option) => option.triageOptionsType === triageOptionsType,
    )?.triageOptionsId;

  const missingCategories: string[] = [];

  const entriesFor = (
    triageOptionsType: string,
    to: RecipientFieldValue[],
    cc: RecipientFieldValue[],
  ): EscalationRecipientRequest[] => {
    const triageOptionsId = optionIdFor(triageOptionsType);
    if (!triageOptionsId) {
      if (to.length || cc.length) missingCategories.push(triageOptionsType);
      return [];
    }
    return [
      ...to.map((user) => ({
        triageOptionsId,
        userId: user.userId,
        role: 'to' as const,
      })),
      ...cc.map((user) => ({
        triageOptionsId,
        userId: user.userId,
        role: 'cc' as const,
      })),
    ];
  };

  const recipients = [
    ...entriesFor(
      TRIAGE_CATEGORY.clinical,
      values.clinicalTo,
      values.clinicalCc,
    ),
    ...entriesFor(
      TRIAGE_CATEGORY.psychosocial,
      values.psychosocialTo,
      values.psychosocialCc,
    ),
    ...entriesFor(
      TRIAGE_CATEGORY.memberManagement,
      values.memberManagementTo,
      values.memberManagementCc,
    ),
  ];

  return { recipients, missingCategories };
};

export const CompanyEscalationPage = () => {
  const { company_id } = useParams();
  const [escalationCompany, setEscalationCompany] = React.useState<
    CompanyInfo | undefined
  >();
  const [escalationUser, setEscalationUser] = React.useState<UserOverview[]>(
    [],
  );
  const [rosterLoaded, setRosterLoaded] = React.useState<boolean>(false);
  const [triageOptions, setTriageOptions] = React.useState<TriageOption[]>([]);
  const [triageOptionsLoading, setTriageOptionsLoading] =
    React.useState<boolean>(true);
  const [triageOptionsError, setTriageOptionsError] =
    React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [pageError, setPageError] = React.useState<string>('');
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [edit, setEdit] = React.useState(false);
  const { handleSubmit, reset, control, clearErrors } = useForm<EscalationForm>(
    {
      mode: 'onChange',
    },
  );
  const companyInfo: CompanyInfo = useSelector(selectCompany);

  const resetForm = () => {
    reset({
      companyPhone: escalationCompany?.companyPhone,
      generalEmail: escalationCompany?.generalEmail,
      googleReviewLink: escalationCompany?.googleReviewLink,
      supportServices: escalationCompany?.supportServices,
      clinicalTriageNotes: escalationCompany?.clinicalTriageNotes,
      psychosocialTriageNotes: escalationCompany?.psychosocialTriageNotes,
      memberManagementTriageNotes:
        escalationCompany?.memberManagementTriageNotes,
      clinicalTo: recipientsForCategory(
        escalationCompany?.recipients,
        TRIAGE_CATEGORY.clinical,
        'to',
      ),
      clinicalCc: recipientsForCategory(
        escalationCompany?.recipients,
        TRIAGE_CATEGORY.clinical,
        'cc',
      ),
      psychosocialTo: recipientsForCategory(
        escalationCompany?.recipients,
        TRIAGE_CATEGORY.psychosocial,
        'to',
      ),
      psychosocialCc: recipientsForCategory(
        escalationCompany?.recipients,
        TRIAGE_CATEGORY.psychosocial,
        'cc',
      ),
      memberManagementTo: recipientsForCategory(
        escalationCompany?.recipients,
        TRIAGE_CATEGORY.memberManagement,
        'to',
      ),
      memberManagementCc: recipientsForCategory(
        escalationCompany?.recipients,
        TRIAGE_CATEGORY.memberManagement,
        'cc',
      ),
    });
    clearErrors();
  };

  const handleEditEscalation = async (values: EscalationForm) => {
    // A stale or failed triage-options fetch means every category's id is
    // unresolvable — submitting anyway would PUT an empty recipient set and
    // wipe whatever is already saved (the backend replaces the full set).
    if (triageOptionsLoading || triageOptionsError) {
      toast.error(
        'Triage categories have not finished loading — try again in a moment.',
      );
      return;
    }

    const { recipients, missingCategories } = buildRecipientsPayload(
      values,
      triageOptions,
    );
    if (missingCategories.length) {
      toast.error(
        `Could not resolve the "${missingCategories.join(
          ', ',
        )}" triage category — nothing was saved. Reload the page and try again.`,
      );
      return;
    }

    // The backend refuses a contact who isn't on the roster, and the only
    // feedback it reaches is "Update failed!". Say who to remove instead.
    if (rosterLoaded) {
      const stale = outOfRoster(
        [
          ...(values.clinicalTo || []),
          ...(values.clinicalCc || []),
          ...(values.psychosocialTo || []),
          ...(values.psychosocialCc || []),
          ...(values.memberManagementTo || []),
          ...(values.memberManagementCc || []),
        ],
        escalationUser,
      );
      if (stale.length) {
        toast.error(
          `Remove ${joinNames(
            stale,
          )} to save: not an active admin of this company or its parent company.`,
        );
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await updateEscalationCompany(
        escalationCompany?.companyId || '',
        {
          companyPhone: values?.companyPhone?.replaceAll(/\D/g, ''),
          generalEmail: values.generalEmail,
          googleReviewLink: values.googleReviewLink,
          supportServices: values.supportServices,
          clinicalTriageNotes: values.clinicalTriageNotes,
          psychosocialTriageNotes: values.psychosocialTriageNotes,
          memberManagementTriageNotes: values.memberManagementTriageNotes,
          recipients,
        },
      );
      if (res.success) {
        setEdit(!res.success);
        fetchCompanyEscalation();
        toast.success('Update success!');
      } else {
        toast.error('Update failed!');
      }
    } catch (error) {
      toast.error('Update failed!');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchCompanyEscalation = async () => {
    try {
      const response = await getEscalationCompany(
        escalationCompany?.companyId || company_id || '',
      );
      setEscalationCompany(response.company);
      setPageError('');
    } catch (error) {
      console.log(error);
      setPageError('Could not load the escalation protocol for this company.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserEscalation = async () => {
    try {
      const response = await getEscalationUsers(
        escalationCompany?.companyId || company_id || '',
      );
      setEscalationUser(response.users || []);
      setRosterLoaded(true);
    } catch (error) {
      console.log(error);
      toast.error('Could not load the recipient roster.');
    }
  };

  const fetchTriageOptions = async () => {
    setTriageOptionsLoading(true);
    setTriageOptionsError(false);
    try {
      const response = await getTriageOptions();
      setTriageOptions(response.triageOptions || []);
    } catch (error) {
      console.log(error);
      setTriageOptionsError(true);
    } finally {
      setTriageOptionsLoading(false);
    }
  };

  const fetchAll = () => {
    setLoading(true);
    setPageError('');
    fetchCompanyEscalation();
    fetchUserEscalation();
    fetchTriageOptions();
  };

  React.useEffect(() => {
    resetForm();
  }, [escalationCompany]);

  React.useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Backdrop
        sx={{
          backgroundColor: 'transparent',
          color: (theme: any) => theme.palette.primary.main,
          zIndex: (theme: any) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <HeaderPage
        title={{
          title: escalationCompany?.name ? escalationCompany?.name : '',
          hasBack: true,
        }}
        tabs={handleTabs(
          company_id || '',
          companyInfo?.isParentCompany || false,
          TabEnum.EscalationProtocol,
        )}
      />

      {!loading && pageError && (
        <div className="nv-container">
          <Stack
            sx={{ padding: '24px 0', gap: '8px', alignItems: 'flex-start' }}
          >
            <Typography sx={{ fontSize: '14px', color: '#7D818C' }}>
              {pageError}
            </Typography>
            <MuiButton
              size="small"
              onClick={fetchAll}
              sx={{
                color: '#356766',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 0,
                minWidth: 0,
              }}
            >
              Try again
            </MuiButton>
          </Stack>
        </div>
      )}

      {!loading && !pageError && escalationCompany && (
        <div className="nv-container">
          <div className="section employees mb-6">
            <form onSubmit={handleSubmit(handleEditEscalation)} noValidate>
              <Stack className="w-[70%]" gap={1.5}>
                <Typography
                  sx={{
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '24px',
                  }}
                >
                  Throughout our conversations with members, Betterleave will
                  use the following information and escalation guidelines to
                  triage inquiries to the appropriate customer team. Please
                  ensure that the information and escalation contacts are up to
                  date to support a smooth transition and timely response.
                </Typography>
                <Stack
                  sx={{
                    marginLeft: 'auto',
                  }}
                  direction={'row'}
                  alignItems={'center'}
                  gap={1}
                >
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: triageOptionsError ? '#CA3838' : '#7D818C',
                      display:
                        edit && (triageOptionsLoading || triageOptionsError)
                          ? 'block'
                          : 'none',
                    }}
                  >
                    {triageOptionsError
                      ? 'Could not load triage categories.'
                      : 'Loading triage categories…'}
                    {triageOptionsError && (
                      <MuiButton
                        size="small"
                        onClick={fetchTriageOptions}
                        sx={{
                          color: '#356766',
                          textTransform: 'none',
                          fontWeight: 600,
                          paddingX: '4px',
                          minWidth: 0,
                        }}
                      >
                        Retry
                      </MuiButton>
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      height: '40px',
                      width: '76px',
                      fontSize: '14px',
                      fontWeight: 700,
                      display: edit ? 'none' : 'block',
                    }}
                    onClick={() => {
                      setEdit(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{
                      height: '40px',
                      width: '110px',
                      fontSize: '14px',
                      fontWeight: 700,
                      display: edit ? 'block' : 'none',
                    }}
                    disabled={submitting}
                    onClick={() => {
                      setEdit(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      height: '40px',
                      width: '110px',
                      fontSize: '14px',
                      fontWeight: 700,
                      display: edit ? 'block' : 'none',
                    }}
                    type="submit"
                    disabled={
                      triageOptionsLoading || triageOptionsError || submitting
                    }
                  >
                    {submitting ? (
                      <CircularProgress size={18} sx={{ color: 'inherit' }} />
                    ) : (
                      'Save'
                    )}
                  </Button>
                </Stack>
                <CustomDivider text="Organization Details" />
                <Typography
                  sx={{
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '24px',
                  }}
                >
                  Betterleave may share organization information with your
                  members throughout our communication.
                </Typography>
                {edit ? (
                  <Stack gap={2} width={'100%'}>
                    <Stack
                      sx={{
                        width: '50%',
                      }}
                      gap={2}
                    >
                      <TextFieldWithMask
                        label="24/7 Business Line"
                        name="companyPhone"
                        format="(###) ### ####"
                        control={control}
                        required
                        placeholder="24/7 Business Line"
                        rules={{
                          required: '24/7 Business Line is required.',
                        }}
                      />
                      <TextField
                        label="General Email"
                        name="generalEmail"
                        control={control}
                        placeholder="General Email"
                        rules={{
                          validate: (value) => {
                            if (!value) return true;
                            if (validateEmails(value)) return true;
                            return 'Please enter valid email';
                          },
                        }}
                      />
                      <TextField
                        label="Google Review Link"
                        name="googleReviewLink"
                        control={control}
                        required
                        placeholder="Google Review Link"
                        rules={{
                          required: 'Google Review Link is required.',
                        }}
                      />
                    </Stack>
                    <TextField
                      name="supportServices"
                      label="Support Services"
                      control={control}
                      placeholder="Support Services"
                      multiline
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#79747E !important',
                        },
                        '& .Mui-error .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#B3261E !important',
                        },
                        input: {
                          height: '24px !important',
                        },
                        textarea: {
                          padding: '0px !important',
                        },
                        width: '100%',
                      }}
                      rows={5}
                    />
                  </Stack>
                ) : (
                  <Stack>
                    <Stack direction={'row'} alignItems={'center'} gap={1}>
                      <Tooltip
                        title={'Missing information'}
                        arrow
                        placement="top"
                        enterTouchDelay={0}
                        sx={{
                          display: escalationCompany?.companyPhone
                            ? 'none'
                            : 'block',
                        }}
                      >
                        <Box component="span" display="inline-flex" p={1}>
                          <ReportProblemOutlinedIcon
                            sx={{
                              color: '#CA3838',
                            }}
                          />
                        </Box>
                      </Tooltip>

                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: 'rgba(125, 129, 140, 1)',
                          marginLeft: escalationCompany?.companyPhone
                            ? '48px'
                            : '0px',
                        }}
                      >
                        24/7 Business Line:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: 'rgba(33, 59, 84, 1)',
                        }}
                      >
                        {formatPhoneNumber(escalationCompany?.companyPhone)}
                      </Typography>
                    </Stack>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      gap={1}
                      marginTop="10px"
                    >
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: 'rgba(125, 129, 140, 1)',
                          marginLeft: '48px',
                        }}
                      >
                        General Email:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: 'rgba(33, 59, 84, 1)',
                        }}
                      >
                        {escalationCompany?.generalEmail}
                      </Typography>
                    </Stack>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      gap={1}
                      marginTop={
                        escalationCompany?.googleReviewLink ? '10px' : '0px'
                      }
                    >
                      <Tooltip
                        title={'Missing information'}
                        arrow
                        placement="top"
                        enterTouchDelay={0}
                        sx={{
                          display: escalationCompany?.googleReviewLink
                            ? 'none'
                            : 'block',
                        }}
                      >
                        <Box component="span" display="inline-flex" p={1}>
                          <ReportProblemOutlinedIcon
                            sx={{
                              color: '#CA3838',
                            }}
                          />
                        </Box>
                      </Tooltip>

                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: 'rgba(125, 129, 140, 1)',
                          marginLeft: escalationCompany?.googleReviewLink
                            ? '48px'
                            : '0px',
                        }}
                      >
                        Google Review Link:
                      </Typography>

                      <a
                        href={escalationCompany?.googleReviewLink || ''}
                        className="tab"
                        style={{
                          color: '#213B54',
                          fontSize: '13px',
                          fontWeight: 700,
                        }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {escalationCompany?.googleReviewLink}
                      </a>
                    </Stack>
                    <Stack
                      direction={'row'}
                      gap={1}
                      marginTop={'10px'}
                      marginLeft={'48px'}
                    >
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: 'rgba(125, 129, 140, 1)',
                          minWidth: { lg: '120px' },
                        }}
                      >
                        Support Services:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          wordBreak: 'break-word',
                          fontWeight: 700,
                          color: '#213B54',
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {escalationCompany?.supportServices}
                      </Typography>
                    </Stack>
                  </Stack>
                )}

                <EscalationTriageSection
                  title="Clinical Triages"
                  description="Betterleave will triage members to the administrators listed below for requests or concerns that require clinical expertise, scheduling assistance, or additional service requests."
                  edit={edit}
                  control={control}
                  toName="clinicalTo"
                  ccName="clinicalCc"
                  notesName="clinicalTriageNotes"
                  roster={escalationUser}
                  rosterLoaded={rosterLoaded}
                />

                <EscalationTriageSection
                  title="Psychosocial Triages"
                  description="Betterleave will triage members to the administrators listed below for requests or concerns related to emotional, social, or family support."
                  edit={edit}
                  control={control}
                  toName="psychosocialTo"
                  ccName="psychosocialCc"
                  notesName="psychosocialTriageNotes"
                  roster={escalationUser}
                  rosterLoaded={rosterLoaded}
                />

                <EscalationTriageSection
                  title="Member Management Triages"
                  description="Betterleave will triage members to the administrators listed below for requests involving member records, enrollment changes, or account management."
                  edit={edit}
                  control={control}
                  toName="memberManagementTo"
                  ccName="memberManagementCc"
                  notesName="memberManagementTriageNotes"
                  roster={escalationUser}
                  rosterLoaded={rosterLoaded}
                />
              </Stack>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
